// App-level error boundary. Reports to Sentry (if configured) and shows a
// minimal recovery screen so a single broken chat doesn't kill the whole app.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/lib/observability';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, { componentStack: info.componentStack ?? '' });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div role="alert" className="min-h-screen flex items-center justify-center p-8 text-center">
        <div>
          <p className="ff-display text-3xl mb-2">Something broke.</p>
          <p className="ff-body text-sm mb-6 opacity-70">
            {this.state.error.message || 'Try refreshing the app.'}
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="px-5 py-2 rounded-full text-white"
            style={{ background: '#E94B3C' }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
